import mapboxgl from 'mapbox-gl';
import key from '../data/mapboxKey'; // eslint-disable-line import/no-webpack-loader-syntax

mapboxgl.accessToken = key;

class Minimap extends mapboxgl.NavigationControl {
  constructor(config) {
    super()
    this.config = config
    this.parentMap = null
    this.miniMap = null
    this.isDragging = false
    this.isCursorOverFeature = false
    this.previousPoint = [0, 0]
    this.currentPoint = [0, 0]
    this.trackingRectCoordinates = [[[], [], [], [], []]]

    this.minimized = false

    this.options = {
      id: "mapbox-minimap",
      width: 320,
      height: 180,
      style: "mapbox://styles/mapbox/streets-v8",
      center: [0, 0],

      zoomLevelFixed: false,
      zoomLevelOffset: -7,

      lineColor: "#08f",
      lineWidth: 1,
      lineOpacity: 1,

      fillColor: "#f80",
      fillOpacity: 0.25,

      dragPan: false,
      scrollZoom: false,
      boxZoom: false,
      dragRotate: false,
      keyboard: false,
      doubleClickZoom: false,
      touchZoomRotate: false,
      minimized: false,
      toggleDisplay: true,
      collapsedWidth: 30,
      collapsedHeight: 30,
      togglePosition: "bottomleft",
      showText: "Show Minimap",
      hideText: "Hide Minimap"
    }

    if (config) {
      Object.assign(this.options, config)
    }
  }

  onAdd(parentMap) {
    this.parentMap = parentMap

    const opts = this.options
    const container = this.createContainer(parentMap)
    this.container = container
    const miniMap = (this.miniMap = new mapboxgl.Map({
      attributionControl: false,
      container: container,
      style: opts.style,
      center: opts.center
    }))

    this.adjustZoom()

    if (opts.maxBounds) {
      miniMap.setMaxBounds(opts.maxBounds)
    }

    this.onLoad = this.load.bind(this)
    miniMap.on("load", this.onLoad)

    if (this.options.toggleDisplay) {
      this.addToggleButton()
    }
	if (this.options.minimized) {
      this.minimize()
    }

    return this.container
  }

  onRemove() {
    this.parentMap.off("move", this.onMainMapMove)
    this.parentMap.off("moveend", this.onMainMapMoveEnd)

    this.miniMap.off("load", this.onLoad)

    this.miniMap.off("mousemove", this.onMouseMove)
    this.miniMap.off("mousedown", this.onMouseDown)
    this.miniMap.off("mouseup", this.onMouseUp)

    this.miniMap.off("touchmove", this.onMouseMove)
    this.miniMap.off("touchstart", this.onMouseDown)
    this.miniMap.off("touchend", this.onMouseUp)

    this.miniMapCanvas.removeEventListener("wheel", this.preventDefault)
    this.miniMapCanvas.removeEventListener("mousewheel", this.preventDefault)

    this.toggleDisplayButton.removeEventListener("click", this.preventDefault)
    this.toggleDisplayButton.removeEventListener(
      "mousedown",
      this.preventDefault
    )
    this.toggleDisplayButton.removeEventListener(
      "dblclick",
      this.preventDefault
    )
    this.toggleDisplayButton.removeEventListener(
      "click",
      this.onToggleButtonClick
    )

    this.container.removeEventListener("contextmenu", this.preventDefault)
    this.container.removeChild(this.toggleDisplayButton)
    this.container.parentNode.removeChild(this.container)
    this.miniMap = null
  }

  addToggleButton() {
    this.toggleDisplayButton = this.options.toggleDisplay
      ? this.createButton(
          "",
          this.toggleButtonInitialTitleText(),
          "minimap mapbox-ctrl-minimap-toggle-display mapbox-ctrl-minimap-toggle-display-" +
            this.options.togglePosition,
          this.container,
          this.toggle,
          this
        )
      : undefined

    this.toggleDisplayButton.style.width = this.options.collapsedWidth-10 + "px"
    this.toggleDisplayButton.style.height = this.options.collapsedHeight-10 + "px"
  }

  createButton(html, title, className, container, fn, context) {
    const link = document.createElement("a")
    link.className = className

    link.innerHTML = html
    link.href = "#"
    link.title = title

    link.addEventListener("click", this.preventDefault)
    link.addEventListener("mousedown", this.preventDefault)
    link.addEventListener("dblclick", this.preventDefault)

    this.onToggleButtonClick = this.toggle.bind(this)
    link.addEventListener("click", this.onToggleButtonClick)

    container.appendChild(link)

    return link
  }

  toggleButtonInitialTitleText() {
    if (this.options.minimized) {
      return this.options.showText
    } else {
      return this.options.hideText
    }
  }

  toggle() {
    if (!this.minimized) {
      this.minimize()
    } else {
      this.restore()
    }
  }

  minimize() {
    if (this.options.toggleDisplay) {
      this.container.style.width = this.options.collapsedWidth + "px"
      this.container.style.height = this.options.collapsedHeight + "px"
      this.toggleDisplayButton.className +=
        " minimized-" + this.options.togglePosition
      this.toggleDisplayButton.title = this.options.showText
    } else {
      this.container.style.display = "none"
    }
    this.minimized = true
  }

  restore() {
    if (this.options.toggleDisplay) {
      this.container.style.width = this.options.width + "px"
      this.container.style.height = this.options.height + "px"
      this.toggleDisplayButton.className = this.toggleDisplayButton.className.replace(
        "minimized-" + this.options.togglePosition,
        ""
      )
      this.toggleDisplayButton.title = this.options.hideText
    } else {
      this.container.style.display = "block"
    }
    this.minimized = false
  }

  changeLayer(layer) {
    this.miniMap.setStyle(layer)
    this.miniMap.on("style.load", () => {
      this.addRect(this.miniMap, this.options)
    })
  }

  remove() {
    this.onRemove()
  }

  load() {
    const opts = this.options
    const parentMap = this.parentMap
    const miniMap = this.miniMap
    const interactions = [
      "dragPan",
      "scrollZoom",
      "boxZoom",
      "dragRotate",
      "keyboard",
      "doubleClickZoom",
      "touchZoomRotate"
    ]

    interactions.forEach(function(i) {
      if (opts[i] !== true) {
        miniMap[i].disable()
      }
    })

    this.addRect(miniMap, opts)

    this.onMainMapMove = this.update.bind(this) // parent map move
    this.onMainMapMoveEnd = this.parentMapMoved.bind(this) // parent map move end

    this.onMouseMove = this.mouseMove.bind(this)
    this.onMouseDown = this.mouseDown.bind(this)
    this.onMouseUp = this.mouseUp.bind(this)

    parentMap.on("move", this.onMainMapMove)
    parentMap.on("moveend", this.onMainMapMoveEnd)

    miniMap.on("mousemove", this.onMouseMove)
    miniMap.on("mousedown", this.onMouseDown)
    miniMap.on("mouseup", this.onMouseUp)

    miniMap.on("touchmove", this.onMouseMove)
    miniMap.on("touchstart", this.onMouseDown)
    miniMap.on("touchend", this.onMouseUp)

    this.miniMapCanvas = miniMap.getCanvasContainer()
    this.miniMapCanvas.addEventListener("wheel", this.preventDefault)
    this.miniMapCanvas.addEventListener("mousewheel", this.preventDefault)
  }

  addRect(miniMap, opts) {
    if (miniMap.getLayer("trackingRectOutline")) {
      miniMap.removeLayer("trackingRectOutline")
    }

    if (miniMap.getLayer("trackingRectFill")) {
      miniMap.removeLayer("trackingRectFill")
    }

    if (miniMap.getSource("trackingRect")) {
      miniMap.removeSource("trackingRect")
    }

    miniMap.addSource("trackingRect", {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {
          name: "trackingRect"
        },
        geometry: {
          type: "Polygon",
          coordinates: this.trackingRectCoordinates
        }
      }
    })

    miniMap.addLayer({
      id: "trackingRectOutline",
      type: "line",
      source: "trackingRect",
      layout: {},
      paint: {
        "line-color": opts.lineColor,
        "line-width": opts.lineWidth,
        "line-opacity": opts.lineOpacity
      }
    })

    // needed for dragging
    miniMap.addLayer({
      id: "trackingRectFill",
      type: "fill",
      source: "trackingRect",
      layout: {},
      paint: {
        "fill-color": opts.fillColor,
        "fill-opacity": opts.fillOpacity
      }
    })

    this.trackingRect = this.miniMap.getSource("trackingRect")

    this.update()
  }

  mouseDown(e) {
    if (this.isCursorOverFeature) {
      this.isDragging = true
      this.previousPoint = this.currentPoint
      this.currentPoint = [e.lngLat.lng, e.lngLat.lat]
    }
  }

  mouseMove(e) {
    const miniMap = this.miniMap
    const features = miniMap.queryRenderedFeatures(e.point, {
      layers: ["trackingRectFill"]
    })

    // don't update if we're still hovering the area
    if (!(this.isCursorOverFeature && features.length > 0)) {
      this.isCursorOverFeature = features.length > 0
      this.miniMapCanvas.style.cursor = this.isCursorOverFeature ? "move" : ""
    }

    if (this.isDragging) {
      this.previousPoint = this.currentPoint
      this.currentPoint = [e.lngLat.lng, e.lngLat.lat]

      const offset = [
        this.previousPoint[0] - this.currentPoint[0],
        this.previousPoint[1] - this.currentPoint[1]
      ]

      var newBounds = this.moveTrackingRect(offset)

      this.parentMap.fitBounds(newBounds, {
        duration: 80
      })
    }
  }

  mouseUp() {
    this.isDragging = false
  }

  moveTrackingRect(offset) {
    var source = this.trackingRect
    var data = source._data
    var bounds = data.properties.bounds

    bounds._ne.lat -= offset[1]
    bounds._ne.lng -= offset[0]
    bounds._sw.lat -= offset[1]
    bounds._sw.lng -= offset[0]

    this.convertBoundsToPoints(bounds)
    source.setData(data)

    return bounds
  }

  setTrackingRectBounds() {
    const bounds = this.parentMap.getBounds()
    const source = this.trackingRect
    const data = source._data

    data.properties.bounds = bounds
    this.convertBoundsToPoints(bounds)
    source.setData(data)
  }

  convertBoundsToPoints(bounds) {
    const ne = bounds._ne
    const sw = bounds._sw
    const trc = this.trackingRectCoordinates

    trc[0][0][0] = ne.lng
    trc[0][0][1] = ne.lat
    trc[0][1][0] = sw.lng
    trc[0][1][1] = ne.lat
    trc[0][2][0] = sw.lng
    trc[0][2][1] = sw.lat
    trc[0][3][0] = ne.lng
    trc[0][3][1] = sw.lat
    trc[0][4][0] = ne.lng
    trc[0][4][1] = ne.lat
  }

  update() {
    if (this.isDragging) {
      return
    }

    if (!this.options.zoomLevelFixed) {
      this.adjustZoom()
    }

    this.setTrackingRectBounds()
  }

  parentMapMoved() {
    this.miniMap.setCenter(this.parentMap.getCenter())
    if (!this.options.zoomLevelFixed) {
      this.adjustZoom()
    }
  }

  adjustZoom() {
    this.miniMap.setZoom(
      this.parentMap.getZoom() + this.options.zoomLevelOffset
    )
  }

  createContainer(parentMap) {
    const opts = this.options
    const container = document.createElement("div")

    container.className = "mapbox-ctrl-minimap mapboxgl-ctrl"
    container.setAttribute(
      "style",
      "width: " + opts.width + "px; height: " + opts.height + "px;"
    )
    container.addEventListener("contextmenu", this.preventDefault)

    parentMap.getContainer().appendChild(container)

    if (opts.id !== "") {
      container.id = opts.id
    }

    return container
  }

  preventDefault(e) {
    e.preventDefault()
  }
}

export {Minimap}
